import type { FC, ReactNode } from "react"
import React from "react"
import type { StackProps } from "@chakra-ui/react"
import { Box, Icon, HStack, Text, Stack } from "@chakra-ui/react"
import capitalize from "lodash.capitalize"

import type { Maybe, VoteStats } from "query/graphql"
import { VoteType } from "query/graphql"
import { getWeightLabel, stringToBigNumber } from "common/helpers/bignumber"
import FilledCircleCheck from "ui/components/icons/FilledCircleCheck"
import { CircleXMark } from "ui/components/icons/font-awesome/solid/CircleXMark"

type VotesCastResultStatusProps = {
  label: string
  isSuccess: boolean
  value?: string
  successColor?: string
  failureColor?: string
}

const VotesCastResultStatus: FC<VotesCastResultStatusProps> = ({
  label,
  isSuccess,
  value,
  successColor = "purple.500",
  failureColor = "#475467",
}) => (
  <HStack justify="space-between">
    <HStack>
      <Icon
        as={isSuccess ? FilledCircleCheck : CircleXMark}
        color={isSuccess ? successColor : failureColor}
      />
      <Text color="475467" fontSize="md" fontWeight="regular">
        {label}
      </Text>
    </HStack>
    {value && (
      <HStack>
        <Text color="475467" fontSize="md" fontWeight="regular">
          {value}
        </Text>
      </HStack>
    )}
  </HStack>
)

// Vote percentage bar component
type VotePercentageBarProps = {
  colorValues: string[]
  voteStats?: Maybe<VoteStats[]>
}

const VotePercentageBar: FC<VotePercentageBarProps> = ({
  voteStats,
  colorValues,
}) => {
  const barValues = voteStats?.map((voteStat, idx) => ({
    label: voteStat.type,
    percentage: voteStat.percent,
    color: colorValues[idx],
  }))

  return (
    <Box
      bg="white"
      borderRadius="md"
      display="flex"
      h={3}
      my={1}
      overflow="hidden"
      w="100%"
    >
      {barValues?.map((value, index) => (
        <Box key={index} bgColor={value.color} w={`${value.percentage}%`} />
      ))}
    </Box>
  )
}

type VoteStatsWithCustomLabel = VoteStats & {
  label?: string
}

// Vote labels component
type VoteLabelsProps = {
  colorValues: string[]
  voteStats?: Maybe<VoteStatsWithCustomLabel[]>
  decimals: number
}

const VoteLabels: FC<VoteLabelsProps> = ({
  voteStats,
  colorValues,
  decimals,
}) => {
  return (
    <Stack spacing={3}>
      {voteStats?.map((voteStat, idx) => {
        const color = colorValues[idx]

        return (
          <HStack key={`vote-label-${idx}`} justify="space-between">
            <HStack>
              <Box bg={color} borderRadius="md" h={4} w={4} />
              <Text color={color} fontSize="md" fontWeight="regular">
                {voteStat.label ?? capitalize(voteStat.type)}
              </Text>
            </HStack>
            <Text color="475467" fontSize="md" fontWeight="regular">
              {getWeightLabel(voteStat.votesCount, decimals)}
            </Text>
          </HStack>
        )
      })}
    </Stack>
  )
}

const getVoteStatsFiltered = (
  voteStats?: VoteStats[] | null,
  isIncludePendingVotes = false,
): VoteStats[] | undefined => {
  if (!voteStats) return undefined

  if (isIncludePendingVotes) {
    const forVoteStats = voteStats.find((stat) => stat.type === VoteType.For)
    const againstVoteStats = voteStats.find(
      (stat) => stat.type === VoteType.Against,
    )
    const abstainVoteStats = voteStats.find(
      (stat) => stat.type === VoteType.Abstain,
    )
    const pendingForVoteStats = voteStats.find(
      (stat) => stat.type === VoteType.Pendingfor,
    )
    const pendingAgainstVoteStats = voteStats.find(
      (stat) => stat.type === VoteType.Pendingagainst,
    )
    const pendingAbstainVoteStats = voteStats.find(
      (stat) => stat.type === VoteType.Pendingabstain,
    )

    return [
      {
        votersCount:
          (forVoteStats?.votersCount ?? 0) +
          (pendingForVoteStats?.votersCount ?? 0),
        type: VoteType.For,
        percent:
          (forVoteStats?.percent ?? 0) + (pendingForVoteStats?.percent ?? 0),
        votesCount: stringToBigNumber(forVoteStats?.votesCount ?? "0")
          .add(stringToBigNumber(pendingForVoteStats?.votesCount ?? "0"))
          .toString(),
      },
      {
        votersCount:
          (againstVoteStats?.votersCount ?? 0) +
          (pendingAgainstVoteStats?.votersCount ?? 0),
        type: VoteType.Against,
        percent:
          (againstVoteStats?.percent ?? 0) +
          (pendingAgainstVoteStats?.percent ?? 0),
        votesCount: stringToBigNumber(againstVoteStats?.votesCount ?? "0")
          .add(stringToBigNumber(pendingAgainstVoteStats?.votesCount ?? "0"))
          .toString(),
      },
      {
        votersCount:
          (abstainVoteStats?.votersCount ?? 0) +
          (pendingAbstainVoteStats?.votersCount ?? 0),
        type: VoteType.Abstain,
        percent:
          (abstainVoteStats?.percent ?? 0) +
          (pendingAbstainVoteStats?.percent ?? 0),
        votesCount: stringToBigNumber(abstainVoteStats?.votesCount ?? "0")
          .add(stringToBigNumber(pendingAbstainVoteStats?.votesCount ?? "0"))
          .toString(),
      },
    ]
  }

  return voteStats.filter(
    (stat) =>
      stat.type !== VoteType.Pendingfor &&
      stat.type !== VoteType.Pendingagainst &&
      stat.type !== VoteType.Pendingabstain,
  )
}

// Types for the main component
type VotesCastResultsProps = {
  decimals: number
  voteStats?: VoteStatsWithCustomLabel[] | null
  isIncludePendingVotes?: boolean
  votesCastResultStatuses: ReactNode
}

// Main component
const VotesCastResults: FC<VotesCastResultsProps & StackProps> = ({
  decimals,
  voteStats,
  isIncludePendingVotes = false,
  votesCastResultStatuses,
  ...stackProps
}) => {
  const colorValues = ["green.600", "red.500", "gray.500"]
  const voteStatsFiltered = getVoteStatsFiltered(
    voteStats,
    isIncludePendingVotes,
  )

  return (
    <Stack as="section" px={4} py={2} spacing={3} w="full" {...stackProps}>
      {votesCastResultStatuses}
      <VotePercentageBar
        colorValues={colorValues}
        voteStats={voteStatsFiltered}
      />
      <VoteLabels
        colorValues={colorValues}
        decimals={decimals}
        voteStats={voteStatsFiltered}
      />
    </Stack>
  )
}

export { VotesCastResults as default, VotesCastResultStatus }
